import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { Box, Button, Chip, Divider, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  ContentCopy as ContentCopyIcon,
  Done as DoneIcon,
  ImageOutlined as ImageOutlinedIcon,
  TextSnippetOutlined as TextSnippetOutlinedIcon,
  TrendingUp as TrendingUpIcon,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import RedditPostKeywords from './RedditPostKeywords';
import type { RedditPost } from '../types';

interface RedditPostItemProps {
  post: RedditPost;
  onLookDeeper: (post: RedditPost) => void;
}

const formatCount = (n: number): string => {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const RedditPostItem: FC<RedditPostItemProps> = ({ post, onLookDeeper }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(post.summary);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }, [post.summary]);

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        backgroundColor: '#fff',
      }}
      elevation={1}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          r/{post.subreddit}
        </Typography>
        <Chip
          icon={post.post_type === 'visual' ? <ImageOutlinedIcon /> : <TextSnippetOutlinedIcon />}
          label={post.post_type === 'visual' ? 'Visual' : 'Text'}
          size="small"
          variant="outlined"
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
        {post.is_trending && (
          <Chip
            icon={<TrendingUpIcon />}
            label="Trending fast"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              bgcolor: 'rgba(255, 152, 0, 0.08)',
              color: 'text.secondary',
              border: '1px solid rgba(255, 152, 0, 0.35)',
              '& .MuiChip-icon': { color: 'rgba(230, 120, 0, 0.7)', fontSize: 13 },
            }}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {format(new Date(post.posted_at), 'MMM d, yyyy')}
        </Typography>
      </Box>

      <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.35 }}>
        {post.title}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
      >
        {post.summary}
      </Typography>

      <RedditPostKeywords keywords={post.keywords} />

      <Divider sx={{ my: 0.5 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {post.author}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ArrowUpwardIcon sx={{ fontSize: 16, color: '#FF4500' }} />
            <Typography variant="caption" fontWeight={600}>
              {formatCount(post.upvotes_count)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {formatCount(post.comments_count)}
            </Typography>
          </Box>
          {post.is_trending && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUpIcon sx={{ fontSize: 15, color: 'success.main' }} />
              <Typography variant="caption" fontWeight={600} color="success.main">
                {post.trend_score.toFixed(2)}
              </Typography>
            </Box>
          )}
          <Tooltip title={copied ? 'Copied!' : 'Copy text'}>
            <IconButton size="small" onClick={handleCopy} aria-label="Copy post text">
              {copied ? (
                <DoneIcon sx={{ fontSize: 16, color: 'success.main' }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{display: 'flex', width: '100%', justifyContent: 'end'}}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ZoomInIcon />}
          onClick={() => {
            onLookDeeper(post);
          }}
          sx={{ alignSelf: 'flex-start', mt: 0.5, textTransform: 'none', fontSize: '0.8125rem' }}
        >
          Look deeper
        </Button>
      </Box>
    </Paper>
  );
};

export default RedditPostItem;
